import { MaterialSymbolIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import dayjs from 'dayjs'
import { Tooltip } from 'antd'
import { OverriddenValueDisplay, OverrideDisplay } from './ValueRenderers'
import type { GroupCellRendererParams } from 'ag-grid-community'
import { formatRate } from '@/components/Freight/Ratebooks/Drill-in/tabs/PointToPoint/utils'
import { useFormatter } from '@/api/useFormatter'
import { isDefined } from '@/utils/general'
import { isPercentageFeeType } from '../../util'

const numberCols = ['amount', 'total', 'rate']

/**
 * Renders the total cell with appropriate styling based on override status
 */
export function OverrideValueCellRenderer({ value, data, colDef, column }: GroupCellRendererParams): JSX.Element {
  if (!data) return <></>
  const fieldName = colDef?.field ?? column?.getColId() ?? ''
  const formatter = useFormatter()

  const hasOverride = isDefined(data[`${fieldName}_override`])
  const isNumber = typeof value === 'number'
  const align = numberCols.includes(fieldName) ? 'right' : 'left'

  let originalValue: number | string = data[`${fieldName}_original`]
  let formattedValue: string | number = value

  switch (fieldName) {
    case 'rate':
      formattedValue = formatRate(value as number, data.uom)
      originalValue = formatRate(originalValue as number, data.uom)
      break
    case 'total':
      formattedValue = formatter.formatCurrency(value as number, true, 2)
      originalValue = formatter.formatCurrency(originalValue as number, true, 2)
      break
    case 'amount':
      formattedValue = isNumber ? (value as number)?.toFixed(2) : (value as string)
      originalValue = (originalValue as number)?.toFixed(2)
      break
  }
  const isSame = formattedValue === originalValue
  const isPercent = isPercentageFeeType(data.uom)
  const materialIconProperties = getMaterialIconProps(fieldName, data.credit_rebill_metadata?.impact_type, isPercent)

  if (data.credit_rebill_metadata?.impact_type && data.credit_rebill_metadata?.impact_type !== 'unchanged' && !isSame) {
    return (
      <Vertical justifyContent='center' style={{ overflow: 'visible' }}>
        <OverriddenValueDisplay value={originalValue} align={align} />
        <OverrideDisplay value={formattedValue} materialIconProperties={materialIconProperties} changeStatus={data.credit_rebill_metadata?.impact_type} align={align} />
      </Vertical>
    )
  }

  if (hasOverride) {
    return (
      <Tooltip title={`Override by ${data.override_by} on ${dayjs(data.override_date).format('MM/DD/YY h:mmA')}`}>
        <div>
          <Vertical justifyContent='center' style={{ overflow: 'visible' }}>
            <OverriddenValueDisplay value={isSame ? null : originalValue} align={align} />
            <OverrideDisplay value={formattedValue} materialIconProperties={materialIconProperties} changeStatus='overridden' align={align} />
          </Vertical>
        </div>
      </Tooltip>
    )
  }

  return (
    <Texto weight={fieldName === 'total' ? 'bold' : 'regular'} align={align}>
      {formattedValue}
    </Texto>
  )
}

const numericColumns = ['amount', 'total', 'rate']

function getMaterialIconProps(columnName: string, changeStatus: string | undefined, isPercent: boolean): {name: MaterialSymbolIcon, color?: string} {
  if (changeStatus === 'removed' && numericColumns.includes(columnName)) {
    return { name: 'arrow_downward', color: 'var(--theme-error)' }
  } else if (changeStatus === 'added' && numericColumns.includes(columnName)) {
    return { name: 'arrow_upward', color: 'var(--theme-success)' }
  } else if (changeStatus === 'impacted' && !isPercent && columnName === 'total' ) { 
    return { name: 'sync', color: 'var(--theme-warning)' }
  }
  return { name: 'person_check', color: 'var(--theme-warning)' }
}